/**
 * Bioteiga Judge.me Reviews — Custom Carousel Element
 *
 * Fetches reviews from the Cloudflare worker proxy and renders them
 * in a horizontally scrollable card carousel matching the theme design.
 */

// In-memory cache for product data (shared across instances during a page load)
const PRODUCT_CACHE = new Map();

async function fetchProduct(handle) {
  if (!handle) return null;
  if (PRODUCT_CACHE.has(handle)) return PRODUCT_CACHE.get(handle);
  const promise = (async () => {
    try {
      const res = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
        headers: { 'Accept': 'application/json' },
      });
      if (!res.ok) return null;
      const p = await res.json();
      return {
        title: p.title,
        url: p.url,
        image: p.featured_image || (p.images && p.images[0]) || null,
      };
    } catch {
      return null;
    }
  })();
  PRODUCT_CACHE.set(handle, promise);
  return promise;
}

class BioteigaJudgemeReviews extends HTMLElement {
  constructor() {
    super();
    this.reviews = [];
    this.currentPage = 1;
    this.hasMore = true;
    this.isLoadingMore = false;
  }

  connectedCallback() {
    this.proxyUrl = this.dataset.proxyUrl;
    this.layout = this.dataset.layout === 'grid' ? 'grid' : 'slider';
    this.perPage = parseInt(this.dataset.perPage, 10) || 30;
    this.gridColumns = parseInt(this.dataset.gridColumns, 10) || 3;
    this.loadMoreText = this.dataset.loadMoreText || 'Mehr Bewertungen anzeigen';
    this.minRating = parseInt(this.dataset.minRating, 10) || 0;
    this.showProduct = this.dataset.showProduct === 'true';
    this.fetchStats = this.dataset.fetchStats === 'true';
    this.summaryEl = document.getElementById(this.dataset.summaryId || '');

    // Layout flag class so CSS can target grid vs slider
    this.classList.toggle('bt-reviews--grid', this.layout === 'grid');
    this.classList.toggle('bt-reviews--slider', this.layout === 'slider');
    if (this.layout === 'grid') {
      this.style.setProperty('--bt-reviews-grid-cols', String(this.gridColumns));
    }

    if (!this.proxyUrl) {
      this._renderStatus('Reviews nicht verfügbar — Proxy URL fehlt.');
      return;
    }

    this._renderSkeleton();
    this._loadReviews();
    if (this.fetchStats && this.summaryEl) this._loadStats();
  }

  async _loadStats() {
    try {
      const res = await fetch(`${this.proxyUrl}/api/stats`, {
        headers: { 'Accept': 'application/json' },
      });
      if (!res.ok) return;
      const data = await res.json();
      if (!data.total_count) return;
      const scoreEl = this.summaryEl.querySelector('[data-summary-score]');
      const countEl = this.summaryEl.querySelector('[data-summary-count]');
      if (scoreEl) scoreEl.textContent = data.average_rating.toFixed(1).replace('.', ',');
      if (countEl) countEl.textContent = `+${data.total_count}`;
    } catch (err) {
      console.warn('[judgeme-reviews] Stats fetch failed:', err);
    }
  }

  async _fetchPage(page) {
    const params = new URLSearchParams({ per_page: String(this.perPage), page: String(page) });
    if (this.minRating === 5) params.set('rating', '5');
    const res = await fetch(`${this.proxyUrl}/api/reviews?${params.toString()}`, {
      headers: { 'Accept': 'application/json' },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    const raw = Array.isArray(data.reviews) ? data.reviews : [];
    let filtered = raw;
    if (this.minRating > 0) {
      filtered = filtered.filter(r => r.rating >= this.minRating);
    }
    // Worker tells us authoritatively whether more pages exist
    const hasMore = data.has_more === true;
    return { raw, filtered, hasMore };
  }

  async _loadReviews() {
    try {
      const { filtered, hasMore } = await this._fetchPage(1);
      this.currentPage = 1;
      this.hasMore = hasMore;

      // Sort newest first (API should do this, but guarantee it)
      filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      this.reviews = filtered;

      if (this.reviews.length === 0) {
        this._renderStatus('Noch keine Bewertungen vorhanden.');
        return;
      }

      this._render();
    } catch (err) {
      console.error('[judgeme-reviews] Fetch failed:', err);
      this._renderStatus('Bewertungen konnten nicht geladen werden.');
    }
  }

  async _loadMore() {
    if (this.isLoadingMore || !this.hasMore) return;
    this.isLoadingMore = true;
    this._setLoadMoreLoading(true);

    try {
      const { filtered, hasMore } = await this._fetchPage(this.currentPage + 1);
      this.currentPage += 1;
      this.hasMore = hasMore;

      // Dedupe by ID — defensive in case the same review appears across pages
      const seen = new Set(this.reviews.map(r => r.id));
      const fresh = filtered.filter(r => !seen.has(r.id));

      this.reviews = this.reviews.concat(fresh);

      // Append new cards to the existing track instead of re-rendering
      if (this.track && fresh.length > 0) {
        const cardsHtml = fresh.map(r => this._buildCard(r)).join('');
        this.track.insertAdjacentHTML('beforeend', cardsHtml);
        if (this.showProduct) this._hydrateProducts();
        this._setupExpand();
      }

      if (!this.hasMore && this.loadMoreContainer) {
        this.loadMoreContainer.remove();
        this.loadMoreContainer = null;
        this.loadMoreBtn = null;
      }
    } catch (err) {
      console.error('[judgeme-reviews] Load more failed:', err);
    } finally {
      this.isLoadingMore = false;
      this._setLoadMoreLoading(false);
    }
  }

  _setLoadMoreLoading(loading) {
    if (!this.loadMoreBtn) return;
    this.loadMoreBtn.classList.toggle('is-loading', loading);
    this.loadMoreBtn.disabled = loading;
  }

  _render() {
    const cards = this.reviews.map(r => this._buildCard(r)).join('');

    if (this.layout === 'grid') {
      this.innerHTML = `
        <div class="bt-reviews__viewport">
          <div class="bt-reviews__track" data-track>
            ${cards}
          </div>
        </div>
        <div class="bt-reviews__load-more" data-load-more-container>
          <button type="button" class="bt-reviews__load-more-btn" data-load-more>
            <span class="bt-reviews__load-more-spinner" aria-hidden="true"></span>
            <span class="bt-reviews__load-more-label">${this._escape(this.loadMoreText)}</span>
          </button>
        </div>
      `;

      this.track = this.querySelector('[data-track]');
      this.loadMoreContainer = this.querySelector('[data-load-more-container]');
      this.loadMoreBtn = this.querySelector('[data-load-more]');
      this.loadMoreBtn?.addEventListener('click', () => this._loadMore());

      if (!this.hasMore && this.loadMoreContainer) {
        this.loadMoreContainer.remove();
        this.loadMoreContainer = null;
        this.loadMoreBtn = null;
      }

      if (this.showProduct) this._hydrateProducts();
      this._setupExpand();
      return;
    }

    // ---- Slider (default) ----
    const showControls = this.reviews.length > 1;

    this.innerHTML = `
      <div class="bt-reviews__viewport">
        <div class="bt-reviews__track" data-track>
          ${cards}
        </div>
      </div>
      ${showControls ? `
        <div class="bt-reviews__controls">
          <button type="button" class="bt-reviews__nav-btn" data-prev aria-label="Vorherige Bewertung" disabled>
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M10 12L6 8l4-4"/>
            </svg>
          </button>
          <div class="bt-reviews__progress" data-progress>
            <div class="bt-reviews__progress-thumb" data-progress-thumb></div>
          </div>
          <button type="button" class="bt-reviews__nav-btn" data-next aria-label="Nächste Bewertung">
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M6 4l4 4-4 4"/>
            </svg>
          </button>
        </div>
      ` : ''}
    `;

    this.track = this.querySelector('[data-track]');
    this.prevBtn = this.querySelector('[data-prev]');
    this.nextBtn = this.querySelector('[data-next]');
    this.progressThumb = this.querySelector('[data-progress-thumb]');

    if (showControls) this._bindCarousel();
    if (this.showProduct) this._hydrateProducts();
    this._setupExpand();
  }

  async _hydrateProducts() {
    // Collect unique handles with at least one card needing product info
    const handles = [...new Set(
      this.reviews
        .map(r => r.product_handle)
        .filter(Boolean)
    )];

    // Kick off all fetches in parallel
    const results = await Promise.all(
      handles.map(async handle => [handle, await fetchProduct(handle)])
    );
    const byHandle = Object.fromEntries(results);

    // Fill in each card's product slot
    this.querySelectorAll('[data-product-slot]').forEach(slot => {
      const handle = slot.dataset.productSlot;
      const product = byHandle[handle];
      if (!product) {
        slot.remove();
        return;
      }
      const imageHtml = product.image
        ? `<img class="bt-reviews__product-image" src="${this._escape(product.image)}" alt="" loading="lazy" width="48" height="48">`
        : '<div class="bt-reviews__product-image bt-reviews__product-image--placeholder" aria-hidden="true"></div>';
      slot.outerHTML = `
        <a class="bt-reviews__product-card" href="${this._escape(product.url)}">
          ${imageHtml}
          <div class="bt-reviews__product-info">
            <span class="bt-reviews__product-label">Bewertetes Produkt</span>
            <span class="bt-reviews__product-title">${this._escape(product.title)}</span>
          </div>
          <svg class="bt-reviews__product-chevron" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M6 4l4 4-4 4"/>
          </svg>
        </a>
      `;
    });
  }

  _buildCard(r) {
    const rating = Math.max(0, Math.min(5, parseInt(r.rating, 10) || 0));
    const stars = Array.from({ length: 5 }, (_, i) =>
      i < rating ? '★' : '<span class="bt-reviews__stars--empty">★</span>'
    ).join('');

    const date = this._formatDate(r.created_at);
    const rawName = r.reviewer?.name || 'Anonym';
    // Rebrand Judge.me's shared "Anonymous" reviewer (used for older imports)
    // to a friendlier club label.
    const authorName = rawName.trim().toLowerCase() === 'anonymous'
      ? 'Natura Moment Club Mitglied'
      : rawName;
    const initial = authorName.trim().charAt(0).toUpperCase() || '?';
    const isVerified = r.reviewer?.verified_buyer || r.verified;

    const title = r.title ? this._escape(r.title) : '';
    const body = this._escape(r.body || '');

    const verifiedBadge = isVerified ? `
      <span class="bt-reviews__verified">
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M3 8l3.5 3.5L13 5"/>
        </svg>
        Verifizierter Kauf
      </span>
    ` : '';

    // Placeholder slot that will be hydrated with product data (image + title)
    const productSlot = this.showProduct && r.product_handle
      ? `<div class="bt-reviews__product-placeholder" data-product-slot="${this._escape(r.product_handle)}"></div>`
      : '';

    // Merchant reply (only present when Judge.me has a shop reply on this review).
    // body_html is sanitized by Judge.me, so we render it as-is.
    let replyBlock = '';
    if (r.reply && r.reply.content) {
      const replyAuthor = this._escape(r.reply.shop_name || 'Antwort vom Shop');
      replyBlock = `
        <div class="bt-reviews__reply">
          <div class="bt-reviews__reply-header">
            <svg class="bt-reviews__reply-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <polyline points="9 14 4 9 9 4"/>
              <path d="M4 9h7a4 4 0 0 1 4 4v0"/>
            </svg>
            <span class="bt-reviews__reply-author">Antwort von ${replyAuthor}</span>
          </div>
          <div class="bt-reviews__reply-body" data-reply-body>${r.reply.content}</div>
          <button type="button" class="bt-reviews__reply-toggle" data-reply-toggle hidden aria-expanded="false">Mehr anzeigen</button>
        </div>
      `;
    }

    return `
      <article class="bt-reviews__card">
        <div class="bt-reviews__card-top">
          <div class="bt-reviews__stars" aria-label="${rating} von 5 Sternen">${stars}</div>
          <span class="bt-reviews__date">${date}</span>
        </div>
        ${title ? `<h3 class="bt-reviews__title">${title}</h3>` : ''}
        <p class="bt-reviews__body" data-body>${body}</p>
        <button type="button" class="bt-reviews__expand-btn" data-expand-btn hidden aria-expanded="false">Mehr anzeigen</button>
        <div class="bt-reviews__footer">
          <span class="bt-reviews__author">
            <span class="bt-reviews__avatar" aria-hidden="true">${initial}</span>
            <span>${this._escape(authorName)}</span>
          </span>
          ${verifiedBadge}
        </div>
        ${replyBlock}
        ${productSlot}
      </article>
    `;
  }

  /**
   * After cards are in the DOM, detect which review bodies (and reply bodies)
   * are actually truncated by line-clamp and reveal the "Mehr anzeigen" button
   * only for those. Uses scrollHeight vs clientHeight to detect overflow.
   */
  _setupExpand(scope = this) {
    const cards = scope.querySelectorAll('.bt-reviews__card');
    cards.forEach(card => {
      if (card.dataset.expandChecked === '1') return;
      card.dataset.expandChecked = '1';

      // Review body
      this._bindClampToggle({
        body: card.querySelector('[data-body]'),
        btn: card.querySelector('[data-expand-btn]'),
        expandedClass: 'is-expanded',
        host: card,
      });

      // Merchant reply body (independent of the review body)
      const replyContainer = card.querySelector('.bt-reviews__reply');
      if (replyContainer) {
        this._bindClampToggle({
          body: replyContainer.querySelector('[data-reply-body]'),
          btn: replyContainer.querySelector('[data-reply-toggle]'),
          expandedClass: 'is-expanded',
          host: replyContainer,
        });
      }
    });
  }

  // Reveals an expand button only when the body is actually overflowing
  // due to line-clamp, then wires up a toggle to expand/collapse.
  _bindClampToggle({ body, btn, expandedClass, host }) {
    if (!body || !btn || !host) return;

    requestAnimationFrame(() => {
      const isTruncated = body.scrollHeight - body.clientHeight > 1;
      if (!isTruncated) return;

      btn.hidden = false;
      btn.addEventListener('click', () => {
        const expanded = host.classList.toggle(expandedClass);
        btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        btn.textContent = expanded ? 'Weniger anzeigen' : 'Mehr anzeigen';
      });
    });
  }

  _bindCarousel() {
    const cardWidth = () => {
      const card = this.track.querySelector('.bt-reviews__card');
      if (!card) return 320;
      const gap = parseFloat(getComputedStyle(this.track).columnGap || '16');
      return card.offsetWidth + gap;
    };

    this.prevBtn.addEventListener('click', () => {
      this.track.scrollBy({ left: -cardWidth(), behavior: 'smooth' });
    });

    this.nextBtn.addEventListener('click', () => {
      this.track.scrollBy({ left: cardWidth(), behavior: 'smooth' });
    });

    const updateProgress = () => {
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      const maxScroll = scrollWidth - clientWidth;
      if (maxScroll <= 0) {
        this.progressThumb.style.width = '100%';
        this.progressThumb.style.left = '0';
        this.prevBtn.disabled = true;
        this.nextBtn.disabled = true;
        return;
      }
      const progress = scrollLeft / maxScroll;
      const visibleRatio = clientWidth / scrollWidth;
      this.progressThumb.style.width = (visibleRatio * 100) + '%';
      this.progressThumb.style.left = (progress * (1 - visibleRatio) * 100) + '%';
      this.prevBtn.disabled = scrollLeft <= 2;
      this.nextBtn.disabled = scrollLeft >= maxScroll - 2;
    };

    this.track.addEventListener('scroll', updateProgress, { passive: true });
    window.addEventListener('resize', updateProgress);
    // Initial
    requestAnimationFrame(updateProgress);
  }

  _renderSkeleton() {
    const cards = Array.from({ length: 4 }, () =>
      '<div class="bt-reviews__skeleton-card"></div>'
    ).join('');
    this.innerHTML = `<div class="bt-reviews__skeleton">${cards}</div>`;
  }

  _renderStatus(message) {
    this.innerHTML = `<div class="bt-reviews__status">${this._escape(message)}</div>`;
  }

  _formatDate(iso) {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return '';
    }
  }

  _escape(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}

if (!window.customElements.get('bioteiga-judgeme-reviews')) {
  window.customElements.define('bioteiga-judgeme-reviews', BioteigaJudgemeReviews);
}
